import {useTranslations} from 'next-intl';
import Container from '@/container';
import {Grid} from '@/grid';
import {SectionPadding} from '@/section';
import Link from '@/links';
import PictureWrapper from '@/ui/images-wrapper';
import StatsStrip from '@/ui/stats-strip';
import {H2, H3, P, PNoMargin} from '@/typography';
import {withBgNeutral200, withNeutral700} from '@/hocs/withColors';
import {withBoldWeight, withHeadingEyebrow, withSecondaryFontTestimonial} from '@/hocs/withFontType';
import styles from '../../industry-shared/customer-stories-section.module.scss';
import localStyles from './customer-stories-section.module.scss';

const Neutral200Section = withBgNeutral200(SectionPadding);
const QuoteP = withSecondaryFontTestimonial(PNoMargin);
const NameP = withBoldWeight(PNoMargin);
const EyebrowNeutral700 = withNeutral700(withHeadingEyebrow(P as never));
const imgPath = '/static/images/industry-construction-and-engineering/customer-stories/';

type StoryStatLabel = {
  label: string;
};

// Stat values are kept inline (not translatable copy) and zipped with the labels from messages.
const STAT_VALUES = ['78%', '41%', '+20%'];

type Testimonial = {
  quote: string;
  nameTitle: string;
  company: string;
  imageUrl: string;
  imageAlt: string;
};

const TESTIMONIALS = [
  {key: 'testimonial1', area: 'testimonial-a'},
  {key: 'testimonial2', area: 'testimonial-b'}
] as const;

/** Grid forwards `children` via createProps; PropTypes-only export omits it from types. */
const FluidGrid = Grid as React.ComponentType<{
  fluid?: boolean;
  className?: string;
  tagName?: string;
  children?: React.ReactNode;
}>;

const CustomerStoriesSection = () => {
  const t = useTranslations('industryConstructionEngineering.customerStories');
  const statLabels = t.raw('featured.stats') as StoryStatLabel[];
  const stats = statLabels.map((stat, index) => ({
    value: STAT_VALUES[index] ?? '',
    label: stat.label
  }));
  const testimonials = TESTIMONIALS.map(({key, area}) => ({
    ...(t.raw(key) as Testimonial),
    area
  }));

  return (
    <Neutral200Section>
      <Container>
        <FluidGrid fluid>
          <H2 className={styles.title}>{t('title')}</H2>

          <div className={localStyles['stories-grid']}>
            <article className={`${styles['featured-card']} ${localStyles.featured}`}>
              <div className={`${styles.media} ${styles['media--featured']}`}>
                <PictureWrapper
                  imgPath={imgPath}
                  responsiveImages={{
                    desktopImageUrl: 'elite-desktop',
                    mobileImageUrl: 'elite-mobile'
                  }}
                  imageTypeFallback="jpg"
                  altImage={t('featured.imageAlt')}
                  className={localStyles['featured-image']}
                  loading="lazy"
                />
              </div>

              <div className={styles['featured-content']}>
                <StatsStrip items={stats} />
                <EyebrowNeutral700 className={styles.eyebrow}>{t('featured.eyebrow')}</EyebrowNeutral700>
                <H3 className={styles['featured-title']}>{t('featured.title')}</H3>
                <P className={styles['featured-copy']}>{t('featured.description')}</P>
                <Link
                  href={t('featured.linkUrl')}
                  variant="cta-default-xs"
                  target={t('featured.linkTarget')}>
                  {t('featured.linkLabel')}
                </Link>
              </div>
            </article>

            {testimonials.map(testimonial => (
              <div key={testimonial.nameTitle} className={localStyles[testimonial.area]}>
                <article className={styles['testimonial-card']}>
                  <div className={styles.media}>
                    <PictureWrapper
                      imgPath={imgPath}
                      imageUrl={testimonial.imageUrl}
                      imageTypeFallback="jpg"
                      altImage={testimonial.imageAlt}
                      className={styles['testimonial-image']}
                      loading="lazy"
                    />
                  </div>

                  <div className={styles['testimonial-content']}>
                    <QuoteP className={styles.quote}>{testimonial.quote}</QuoteP>
                    <NameP className={styles.name}>{testimonial.nameTitle}</NameP>
                    <PNoMargin className={styles.company}>{testimonial.company}</PNoMargin>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </FluidGrid>
      </Container>
    </Neutral200Section>
  );
};

export default CustomerStoriesSection;
